import { useEffect } from "react";
import { useRestApiProvider, UserStatus } from "@jugl-web/rest-api";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import usePhoenixSocket from "@web-src/modules/socket/providers/usePhoenixSocket";
import { PhxResponse } from "@web-src/modules/socket/providers/PhoenixSocket";
import { useDispatch } from "react-redux";
import { useRequestReSync } from "@jugl-web/utils";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { useUserStatusFormatString } from "./useUserStatusFormatString";

export const useUserStatus = (
  userId?: string,
  shouldSkip?: boolean
): {
  status: UserStatus | undefined;
  isOnline: boolean;
  formattedString: string;
} | null => {
  const { usersApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { channel, socketConnected } = usePhoenixSocket();

  const formatUserStatus = useUserStatusFormatString();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<ThunkDispatch<any, unknown, AnyAction>>();

  const { data: userStatus, refetch } = usersApi.useGetUserStatusQuery(
    userId && socketConnected && !shouldSkip
      ? { entityId, userId, silentError: true }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: 60000,
    }
  );
  useRequestReSync(() => refetch());

  useEffect(() => {
    if (!channel || !userId || shouldSkip) return undefined;

    channel.push("sub_status", {
      user_list: [userId],
    });

    const handleStatusUpdate = (e: PhxResponse<UserStatus[]>) => {
      if (e.status !== "ok") {
        return;
      }

      e.response.forEach((status) => {
        dispatch(
          usersApi.util.updateQueryData(
            "getUserStatus",
            { entityId: status.entity_id, userId: status.user_id },
            () => status
          )
        );
      });
    };

    const userStatusListenerId = channel.on("phx_status", handleStatusUpdate);

    return () => channel.off("phx_status", userStatusListenerId);
  }, [channel, userId, shouldSkip, dispatch, entityId, usersApi]);

  return userStatus
    ? {
        status: userStatus,
        isOnline: userStatus.status === "Online",
        formattedString: formatUserStatus(userStatus),
      }
    : null;
};
